import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Paper, Button, CircularProgress, IconButton, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Add, Edit, PersonAdd, Delete } from '@mui/icons-material';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

const Users = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [toggleDialog, setToggleDialog] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchUsers();
    const interval = setInterval(() => {
      if (localStorage.getItem("usersNeedRefresh") === "true") {
        localStorage.removeItem("usersNeedRefresh");
        fetchUsers();
      }
    }, 500);
    return () => clearInterval(interval);
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await api.get('/api/v1/users/');
      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (user) => {
    try {
      const res = await api.patch(`/api/v1/users/${user.id}`, { password: '' });
      const newPw = res.data.password || res.data.generated_password;
      if (newPw) {
        toast.success(`New password for ${user.email}: ${newPw}`, { duration: 10000 });
        navigator.clipboard.writeText(newPw);
        toast('Password copied to clipboard', { duration: 3000 });
      }
    } catch (err) {
      toast.error('Failed to reset password');
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog) return;
    setDeleting(true);
    try {
      await api.delete(`/api/v1/users/${deleteDialog.id}`);
      toast.success('User deleted');
      fetchUsers();
    } catch (err) {
      toast.error('Failed to delete user');
    } finally {
      setDeleting(false);
      setDeleteDialog(null);
    }
  };

  const handleToggleStatus = async () => {
    if (!toggleDialog) return;
    try {
      await api.patch(`/api/v1/users/${toggleDialog.id}`, { is_active: !toggleDialog.is_active });
      toast.success(toggleDialog.is_active ? 'User deactivated' : 'User activated');
      fetchUsers();
    } catch (err) {
      toast.error('Failed');
    } finally {
      setToggleDialog(null);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={600} color="#2c3e50">👥 User Management</Typography>
        <Button onClick={() => navigate('/users/new')} variant="contained" startIcon={<PersonAdd />} sx={{ bgcolor: '#1abc9c', '&:hover': { bgcolor: '#16a085' } }}>
          Add User
        </Button>
      </Box>

      {users.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="textSecondary">No users yet. Add your first user.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: '10px', border: '1px solid #e0e0e0' }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: '#34495e' }}>
              <TableRow>
                <TableCell sx={{ color: 'white', fontWeight: 500 }}>Name</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 500 }}>Email</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 500 }}>Role</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 500 }}>Department</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 500 }}>Entity</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 500 }}>Status</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 500 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map(u => (
                <TableRow key={u.id} hover sx={{ opacity: u.is_active ? 1 : 0.5 }}>
                  <TableCell sx={{ fontWeight: 500 }}>
                    {[u.first_name, u.last_name].filter(Boolean).join(' ') || '—'}
                  </TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>
                    <Chip label={u.role === 'enterprise_admin' ? 'Admin' : u.role} size="small" color={u.role === 'enterprise_admin' ? 'primary' : 'default'} />
                    {u.is_validator && <Chip label="Validator" size="small" color="success" sx={{ ml: 0.5 }} />}
                  </TableCell>
                  <TableCell>{u.department || '—'}</TableCell>
                  <TableCell>{u.entity || '—'}</TableCell>
                  <TableCell>
                    <Chip label={u.is_active ? 'Active' : 'Inactive'} size="small" color={u.is_active ? 'success' : 'error'} />
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={() => navigate(`/users/${u.id}/edit`)} size="small"><Edit fontSize="small" /></IconButton>
                    <Button onClick={() => handleResetPassword(u)} size="small" variant="text" sx={{ fontSize: '11px', color: '#e67e22' }}>Reset PW</Button>
                    <Button onClick={() => setToggleDialog(u)} size="small" color={u.is_active ? 'error' : 'success'} variant="text" sx={{ fontSize: '11px' }}>{u.is_active ? 'Deactivate' : 'Activate'}</Button>
                    <IconButton onClick={() => setDeleteDialog(u)} size="small" color="error"><Delete fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={!!toggleDialog} onClose={() => setToggleDialog(null)}>
        <DialogTitle>{toggleDialog?.is_active ? 'Deactivate' : 'Activate'} User</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to {toggleDialog?.is_active ? 'deactivate' : 'activate'} <strong>{toggleDialog?.email}</strong>?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToggleDialog(null)}>Cancel</Button>
          <Button onClick={handleToggleStatus} variant="contained" color={toggleDialog?.is_active ? 'error' : 'success'}>Confirm</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteDialog} onClose={() => setDeleteDialog(null)}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete <strong>{deleteDialog?.email}</strong>?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(null)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users;
